import * as React from "react";
import {useId, useState} from "react";
import type {FieldError} from "@/shared/timr";
import {FormGroup} from "@/shared/ui/form/FormGroup";
import {DatePickerInput} from "@/shared/ui/primitives/DatePickerInput";
import {
	applyDateChange,
	applyHalfDayChange,
	cloneDateRangeValue,
	isSingleDayHalfDayRange,
	shouldShowEndHalfDay,
	type DateRangeValue
} from "@/shared/ui/dateRange/dateRangeState";
import {clsx} from "clsx";
import {Temporal} from "@/shared/utils/datefunctions";

interface DateRangePanelProps {
	/** The current date range being edited; controlled — the parent holds it and passes back each {@link DateRangePanelProps.onValueChange}. */
	value: DateRangeValue;
	/** Server-side validation errors to surface on the matching fields, keyed by field name (`start`, `startDate`, …). */
	fieldErrors: FieldError[];
	/** Renders the fields read-only for the current record; orthogonal to the edited data. */
	disabled: boolean;
	/** Called after every user edit with the next committed value */
	onValueChange: (value: DateRangeValue) => void;
	/** Label for the start-date field. */
	startLabel: string;
	/** Label for the end-date field. */
	endLabel: string;
	/** Whether the half-day checkboxes apply for the record being edited; false renders a plain whole-day range. */
	halfDayMode: boolean;
}

function fieldError(fieldErrors: FieldError[], ...fieldNames: string[]): string | undefined {
	return fieldErrors.find(error => fieldNames.includes(error.field))?.message;
}

/**
 * One side (start or end) of the range: a date picker, plus – in half-day mode – the half-day checkbox.
 * Endpoint-agnostic beyond labeling/ids — the caller resolves the value slice and the half-day-specific
 * pieces (checked/visible/label all need the full range to know things like "is this a single-day range"),
 * and wraps each callback with whichever `apply*` state transition applies to its endpoint.
 */
function DateRangeFields({
	endpoint,
	idPrefix,
	value,
	onDateChange,
	onHalfDayChange,
	fieldErrors,
	disabled,
	halfDayMode,
	halfDayChecked,
	halfDayVisible,
	halfDayLabel,
	label
}: Readonly<{
	endpoint: "start" | "end";
	idPrefix: string;
	value: Temporal.PlainDate | null;
	onDateChange: (value: Temporal.PlainDate | null) => void;
	onHalfDayChange: (checked: boolean) => void;
	fieldErrors: FieldError[];
	disabled: boolean;
	halfDayMode: boolean;
	halfDayChecked: boolean;
	halfDayVisible: boolean;
	halfDayLabel: string;
	label: string;
}>) {
	const dateId = `${idPrefix}${endpoint}Date`;

	return (
		<FormGroup
			label={label}
			htmlFor={dateId}
			error={fieldError(fieldErrors, endpoint, `${endpoint}Date`)}
		>
			<div className="dateTimeRow">
				<div className="row">
					<div className={`${halfDayMode ? "col-md-6" : "col-md-12"} dateTimeGroup`}>
						<div className="input-group">
							<div className="input-group-btn">
								<label htmlFor={dateId} className={clsx("btn btn-default btn-icon", disabled && "disabled")}>
									<i className="far fa-calendar-day" />
								</label>
							</div>
							<DatePickerInput
								id={dateId}
								value={value}
								onValueChange={onDateChange}
								className={`dateField rangeDate border-radius-right`}
								disabled={disabled}
							/>
						</div>
					</div>
					{halfDayMode && (
						<div className="col-md-6 durationUnit-halfDays">
							<label className="checkbox-inline" style={{display: halfDayVisible ? undefined : "none"}}>
								<input
									type="checkbox"
									className={`form-control rangeHalfDay`}
									checked={halfDayChecked}
									disabled={disabled}
									onChange={event => onHalfDayChange(event.target.checked)}
								/>
								{halfDayLabel}
							</label>
						</div>
					)}
				</div>
			</div>
		</FormGroup>
	);
}

/** The two per-endpoint edits, each needing the full range to know things like the other endpoint's date. */
function endpointCallbacks(value: DateRangeValue, endpoint: "start" | "end", halfDayMode: boolean, commit: (next: DateRangeValue) => void) {
	return {
		onDateChange: (date: Temporal.PlainDate | null) => commit(applyDateChange(value, endpoint, date, halfDayMode)),
		onHalfDayChange: (checked: boolean) => commit(applyHalfDayChange(value, endpoint, checked, halfDayMode))
	};
}

export function DateRangePanel({value: seededValue, fieldErrors, disabled, onValueChange, startLabel, endLabel, halfDayMode}: Readonly<DateRangePanelProps>) {
	const idPrefix = useId();

	// The panel owns the working value: the parent seeds it and hears about edits via onValueChange, but
	// does not re-feed on every keystroke. A NEW seededValue object is the reseed signal (dialog open,
	// server errors); comparing during render instead of in an effect avoids committing a frame with the
	// stale previous value.
	const [value, setValue] = useState<DateRangeValue>(() => cloneDateRangeValue(seededValue));
	const [lastSeededValue, setLastSeededValue] = useState(seededValue);
	if (lastSeededValue !== seededValue) {
		setLastSeededValue(seededValue);
		setValue(cloneDateRangeValue(seededValue));
	}

	const commit = (next: DateRangeValue) => {
		setValue(next);
		onValueChange(next);
	};

	const endpointProps = {idPrefix, fieldErrors, disabled, halfDayMode};

	return (
		<div className="date-range-panel">
			<DateRangeFields
				endpoint="start"
				{...endpointProps}
				value={value.start}
				halfDayChecked={value.startHalfDay}
				halfDayVisible={true}
				halfDayLabel={isSingleDayHalfDayRange(value, halfDayMode) ? messages["workingTime.halfDay"] : messages["workingTime.startHalfDay"]}
				label={startLabel}
				{...endpointCallbacks(value, "start", halfDayMode, commit)}
			/>
			<DateRangeFields
				endpoint="end"
				{...endpointProps}
				value={value.end}
				halfDayChecked={value.endHalfDay}
				halfDayVisible={shouldShowEndHalfDay(value, halfDayMode)}
				halfDayLabel={messages["workingTime.endHalfDay"]}
				label={endLabel}
				{...endpointCallbacks(value, "end", halfDayMode, commit)}
			/>
		</div>
	);
}

export type {DateRangePanelProps};
